import * as React from 'react';
import { Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { formValueSelector, Field } from 'redux-form';

import type { EmployeeOptionVM } from 'ab-viewModels/employee/option.viewModel';

import * as TimeOptionUtils from '@acceligentllc/shared/utils/timeOption';

import { DAY_SHIFT, NIGHT_SHIFT } from '@acceligentllc/shared/enums/shift';
import { TimeOptionValues } from '@acceligentllc/shared/enums/timeOption';

import type { RootState } from 'af-reducers';

import { WORK_ORDER_FORM } from 'af-constants/reduxForms';

import Dropdown from 'af-fields/Dropdown';

import DropdownEmployeeItem from 'af-components/DropdownEmployeeItem';

import * as EmployeeActions from 'af-actions/employee';

import Shift from './Shift';
import Section from '../../Shared/Section';

const TimeOptions = Object.keys(TimeOptionValues).map((key) => ({ id: TimeOptionUtils.toNumber(key), name: TimeOptionValues[key] }));

interface Item {
	label: string;
	value: string | number;
}

interface OwnProps {
	disabled: boolean;
	resetTimer: () => void;
	change: (fieldName: string, value: string | number | null) => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const TIME_TO_START_CONTAINER = 'timeToStart-container';
const TIME_TO_END_CONTAINER = 'timeToEnd-container';
const FILTER_PM_BY = ['firstName', 'lastName', 'fullName', 'formattedCode', 'accountTemplate'];

const WorkOrderGeneral: React.FC<Props> = (props) => {

	const {
		disabled,
		valueSelector,
		resetTimer,
		change: _change,
		shift,
		dayShiftStart,
		dayShiftEnd,
		nightShiftStart,
		nightShiftEnd,
		loadProjectManagers,
	} = props;

	const change = React.useCallback((fieldName: string, value: string | number | null) => {
		_change(fieldName, value);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [projectManagers, setProjectManagers] = React.useState<EmployeeOptionVM[]>([]);

	const onShiftChange = React.useCallback((newValue: Item) => {
		if (newValue?.value === DAY_SHIFT.id) {
			change('timeToStart', dayShiftStart ?? null);
			change('timeToEnd', dayShiftEnd ?? null);
		} else if (newValue?.value === NIGHT_SHIFT.id) {
			change('timeToStart', nightShiftStart ?? null);
			change('timeToEnd', nightShiftEnd ?? null);
		}
		resetTimer();
	}, [change, dayShiftEnd, dayShiftStart, nightShiftEnd, nightShiftStart, resetTimer]);

	const lazyLoadProjectManagers = React.useCallback(async (isLazyLoaded: boolean) => {
		if (!isLazyLoaded) {
			const _projectManagers = await loadProjectManagers();
			setProjectManagers(_projectManagers);
		}
	}, [loadProjectManagers]);

	const getSelectedStartTimeValue = React.useCallback(() => {
		const selectedNumber = valueSelector('timeToStart') as number;
		const selected = TimeOptionUtils.toString(selectedNumber);
		return TimeOptionValues[selected];
	}, [valueSelector]);

	const getSelectedEndTimeValue = React.useCallback(() => {
		const selectedNumber = valueSelector('timeToEnd') as number;
		const selected = TimeOptionUtils.toString(selectedNumber);
		return TimeOptionValues[selected];
	}, [valueSelector]);

	const onStartTimeSelected = React.useCallback((timeOption: typeof TimeOptions[0]) => {
		change('timeToStart', timeOption.id);
		resetTimer();
	}, [change, resetTimer]);

	const onEndTimeSelected = React.useCallback((timeOption: typeof TimeOptions[0]) => {
		change('timeToEnd', timeOption.id);
		resetTimer();
	}, [change, resetTimer]);

	const renderEmployee = React.useCallback((option: EmployeeOptionVM) => {
		return <DropdownEmployeeItem option={option} showUserRole={true} />;
	}, []);

	const renderSelectedEmployee = React.useCallback((option: EmployeeOptionVM) => {
		return <DropdownEmployeeItem option={option} showUserRole={false} />;
	}, []);

	const isCustomShift = shift?.id !== DAY_SHIFT.id && shift?.id !== NIGHT_SHIFT.id;

	return (
		<Section
			label="General"
			tooltip="Core Work Order details. Project Manager responsible for overseeing this Work Order, the Shift (Day, Night, or Other), and scheduled work hours. This information will be visible on the timesheet in the Field Report."
		>
			<Col md={8}>
				<Field
					component={Dropdown}
					containerId="work-order__project-manager"
					disabled={disabled}
					dropdownClassName="work-order-upsert__user-type-dropdown"
					dropdownId="work-order__project-manager--dropdown"
					filterable={true}
					filterBy={FILTER_PM_BY}
					hasBlankOption={true}
					id="projectManagerId"
					label="Project Manager"
					name="projectManagerId"
					onLazyLoad={lazyLoadProjectManagers}
					onValueChange={resetTimer}
					options={projectManagers}
					placeholder="Select Project Manager"
					propName="projectManager"
					renderMenuItem={renderEmployee}
					renderSelected={renderSelectedEmployee}
					valueKey="id"
					withCaret={true}
				/>
			</Col>
			<Col md={isCustomShift ? 8 : 6}>
				<Shift disabled={disabled} onValueChange={onShiftChange} />
			</Col>
			<Col id={TIME_TO_START_CONTAINER} md={3} xs={12}>
				<Field
					component={Dropdown}
					disabled={disabled}
					getSelectedItemIndex
					getSelectedValue={getSelectedStartTimeValue}
					id="timeToStart"
					label="Work Time Start"
					labelKey="name"
					name="timeToStart"
					onValueChange={onStartTimeSelected}
					options={TimeOptions}
					shouldScrollToSelectedItem={true}
					valueKey="id"
					withCaret={true}
				/>
			</Col>
			<Col id={TIME_TO_END_CONTAINER} md={3} xs={12}>
				<Field
					component={Dropdown}
					disabled={disabled}
					getSelectedValue={getSelectedEndTimeValue}
					id="timeToEnd"
					label="Work Time End"
					labelKey="name"
					name="timeToEnd"
					onValueChange={onEndTimeSelected}
					options={TimeOptions}
					shouldScrollToSelectedItem={true}
					valueKey="id"
					withCaret={true}
				/>
			</Col>
		</Section>
	);
};

const selector = formValueSelector(WORK_ORDER_FORM);

function mapStateToProps(state: RootState) {
	const { company } = state.company;

	const shift = selector(state, 'shift');

	return {
		dayShiftStart: company?.dayShiftStart,
		dayShiftEnd: company?.dayShiftEnd,
		nightShiftStart: company?.nightShiftStart,
		nightShiftEnd: company?.nightShiftEnd,
		shift,
		valueSelector: (fieldName: string) => selector(state, fieldName),
	};
}

function mapDispatchToProps() {
	return {
		loadProjectManagers: EmployeeActions.findAllProjectManagers,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(connector);

export default enhance(WorkOrderGeneral);
